import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { HomeCommand, HomeState } from "@home-control/shared";
import { AuthenticationError, fetchCommandResult, fetchState, login, sendCommand } from "../api";
import {
  applyOptimisticCommand,
  applyPendingCommands,
  verifiedPendingLabels,
  type PendingCommands
} from "./pending";

const tokenKey = "home-control-token";
const stateCacheKey = "home-control-state-cache";
const pendingPollMs = 850;
const pendingPollAttempts = 18;
const statePollMs = 5_000;
const toastDismissMs = 5_000;

export const controlsGatedMessage = "Updating latest climate data.";

interface HomeContextValue {
  token: string;
  sessionNotice: string;
  state: HomeState | null;
  error: string;
  success: string;
  /** True while a manual (refresh-button) fetch is in flight. */
  refreshing: boolean;
  /** True once at least one live fetch attempt has completed. */
  hasLoadedOnce: boolean;
  /** Live state is on screen; cached state renders read-only. */
  canControlDevices: boolean;
  isShowingCachedState: boolean;
  pendingCommands: PendingCommands;
  isPending: (label: string) => boolean;
  command: (label: string, payload: HomeCommand, successMessage?: string) => Promise<void>;
  refresh: () => Promise<void>;
  submitLogin: (password: string) => Promise<void>;
  dismissError: () => void;
  dismissSuccess: () => void;
}

const HomeContext = createContext<HomeContextValue | null>(null);

export function useHome(): HomeContextValue {
  const value = useContext(HomeContext);
  if (!value) throw new Error("useHome must be used inside HomeStateProvider");
  return value;
}

function readCachedState(): HomeState | null {
  try {
    const raw = localStorage.getItem(stateCacheKey);
    return raw ? (JSON.parse(raw) as HomeState) : null;
  } catch {
    return null;
  }
}

export function HomeStateProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState(() => localStorage.getItem(tokenKey) ?? "");
  const [state, setState] = useState<HomeState | null>(() => readCachedState());
  const [isShowingCachedState, setIsShowingCachedState] = useState(() => readCachedState() !== null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sessionNotice, setSessionNotice] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [pendingCommands, setPendingCommands] = useState<PendingCommands>({});
  const pendingRef = useRef<PendingCommands>({});

  const canControlDevices = state !== null && !isShowingCachedState;

  function updatePending(updater: (current: PendingCommands) => PendingCommands) {
    const next = updater(pendingRef.current);
    pendingRef.current = next;
    setPendingCommands(next);
  }

  function expireSession(message: string) {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(stateCacheKey);
    setToken("");
    setState(null);
    setIsShowingCachedState(false);
    updatePending(() => ({}));
    setError("");
    setSuccess("");
    setSessionNotice(message);
  }

  async function refreshState(authToken = token, pending = pendingRef.current): Promise<HomeState | null> {
    if (!authToken) return null;
    try {
      const nextState = await fetchState(authToken);
      setHasLoadedOnce(true);
      if (!nextState) {
        localStorage.removeItem(stateCacheKey);
        setIsShowingCachedState(false);
        setState(null);
        return null;
      }

      const verifiedLabels = verifiedPendingLabels(nextState, pending);
      const verified = new Set(verifiedLabels);
      const unverified = Object.fromEntries(Object.entries(pending).filter(([label]) => !verified.has(label)));
      setState(applyPendingCommands(nextState, unverified));
      localStorage.setItem(stateCacheKey, JSON.stringify(nextState));
      setIsShowingCachedState(false);
      if (verifiedLabels.length) {
        updatePending((current) => removePendingLabels(current, pending, verifiedLabels));
      }
      setError("");
      return nextState;
    } catch (refreshError) {
      setHasLoadedOnce(true);
      if (refreshError instanceof AuthenticationError) {
        expireSession("Saved sign-in was reset. Enter the password to reconnect.");
        return null;
      }
      setError("Unable to reach the house.");
      return null;
    }
  }

  useEffect(() => {
    void refreshState();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void refreshState();
    }, statePollMs);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void refreshState();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [token]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(""), toastDismissMs);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(""), toastDismissMs);
    return () => clearTimeout(timer);
  }, [success]);

  async function submitLogin(password: string) {
    const nextToken = await login(password);
    localStorage.setItem(tokenKey, nextToken);
    setToken(nextToken);
    setSessionNotice("");
    await refreshState(nextToken);
  }

  async function refresh() {
    setRefreshing(true);
    try {
      await refreshState();
    } finally {
      setRefreshing(false);
    }
  }

  async function command(label: string, payload: HomeCommand, successMessage = "Command sent") {
    if (!canControlDevices) {
      setSuccess("");
      setError(controlsGatedMessage);
      return;
    }
    updatePending((current) => ({ ...current, [label]: payload }));
    const previousState = state;
    setState((current) => (current ? applyOptimisticCommand(current, payload) : current));
    try {
      const id = await sendCommand(token, payload);
      setError("");
      void verifyCommand(label, payload, token, id, successMessage);
    } catch (commandError) {
      setState(previousState);
      updatePending((current) => removePendingLabels(current, { [label]: payload }, [label]));
      if (commandError instanceof AuthenticationError) {
        expireSession("Saved sign-in was reset. Enter the password to reconnect.");
        return;
      }
      setError("Command was not accepted.");
    }
  }

  async function verifyCommand(
    label: string,
    payload: HomeCommand,
    authToken: string,
    commandId: string,
    successMessage: string
  ) {
    for (let attempt = 0; attempt < pendingPollAttempts; attempt += 1) {
      await delay(pendingPollMs);
      if (!sameCommand(pendingRef.current[label], payload)) {
        // Verified by an interleaved refresh (or superseded by a newer command).
        if (pendingRef.current[label] === undefined) setSuccess(successMessage);
        return;
      }
      const result = await readCommandResult(authToken, commandId);
      if (result === "unavailable") {
        continue;
      }
      if (result === "auth") {
        expireSession("Saved sign-in was reset. Enter the password to reconnect.");
        return;
      }
      if (result && !result.ok) {
        updatePending((current) => removePendingLabels(current, { [label]: payload }, [label]));
        await refreshState(authToken, {});
        setError(commandErrorMessage(result.error));
        return;
      }
      const latestState = await refreshState(authToken, pendingRef.current);
      if (latestState && verifiedPendingLabels(latestState, { [label]: payload }).length > 0) {
        setSuccess(successMessage);
        return;
      }
    }

    if (sameCommand(pendingRef.current[label], payload)) {
      updatePending((current) => removePendingLabels(current, { [label]: payload }, [label]));
      await refreshState(authToken, {});
      setError("Change was not confirmed.");
    }
  }

  const value: HomeContextValue = {
    token,
    sessionNotice,
    state,
    error,
    success,
    refreshing,
    hasLoadedOnce,
    canControlDevices,
    isShowingCachedState,
    pendingCommands,
    isPending: (label) => Boolean(pendingCommands[label]),
    command,
    refresh,
    submitLogin,
    dismissError: () => setError(""),
    dismissSuccess: () => setSuccess("")
  };

  return <HomeContext.Provider value={value}>{children}</HomeContext.Provider>;
}

function removePendingLabels(
  current: PendingCommands,
  expected: PendingCommands,
  labels: string[]
): PendingCommands {
  const next = { ...current };
  for (const label of labels) {
    if (sameCommand(current[label], expected[label])) {
      delete next[label];
    }
  }
  return next;
}

function sameCommand(left: HomeCommand | undefined, right: HomeCommand | undefined): boolean {
  return Boolean(left && right && JSON.stringify(left) === JSON.stringify(right));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function readCommandResult(authToken: string, commandId: string) {
  try {
    return await fetchCommandResult(authToken, commandId);
  } catch (error) {
    if (error instanceof AuthenticationError) {
      return "auth" as const;
    }
    return "unavailable" as const;
  }
}

function commandErrorMessage(error: string | undefined): string {
  if (!error) return "Command failed.";
  return error;
}
